import { IContactCenterUsageSummary } from "@components/superadmin/billing/interfaces/billingSubscriptions.interface";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import PhoneCallbackOutlinedIcon from "@mui/icons-material/PhoneCallbackOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import VoicemailOutlinedIcon from "@mui/icons-material/VoicemailOutlined";
import { Box, Card, CircularProgress, Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import React from "react";

type Props = {
  usage?: IContactCenterUsageSummary;
  isLoading?: boolean;
  isError?: boolean;
};

const formatNumber = (value?: number) => {
  return new Intl.NumberFormat("en-US").format(Number(value ?? 0));
};

const formatMoney = (value?: number, currency = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(Number(value ?? 0));
};

const UsageCard = ({ label, value, helper, icon, color }: { label: string; value: string; helper: string; icon: React.ReactNode; color: string }) => {
  return (
    <Card
      elevation={0}
      sx={{
        p: 2.25,
        height: "100%",
        borderRadius: "16px",
        border: "none",
        bgcolor: "var(--fx-surface-base, #FFFFFF)",
      }}
    >
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: "var(--fx-surface-textsecondary, #64748B)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            {label}
          </Typography>

          <Typography
            sx={{
              mt: 0.5,
              fontSize: "1.65rem",
              fontWeight: 900,
              color: "var(--fx-surface-text, #0F172A)",
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 1,
              color: "var(--fx-surface-textsecondary, #64748B)",
              fontWeight: 600,
              lineHeight: 1.35,
            }}
          >
            {helper}
          </Typography>
        </Box>

        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: "14px",
            bgcolor: `${color}14`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Card>
  );
};

export const ContactCenterUsageCards = ({ usage, isLoading, isError }: Props) => {
  const totals = usage?.totals;

  if (isError) {
    return (
      <Card
        elevation={0}
        sx={{
          mb: 3.5,
          p: 2.5,
          borderRadius: "16px",
          border: "none",
          bgcolor: "var(--fx-colors-danger-50, #FEF2F2)",
        }}
      >
        <Typography sx={{ fontWeight: 800, color: "#991B1B" }}>Failed to load communication usage.</Typography>
      </Card>
    );
  }

  return (
    <Box sx={{ mb: 3.5 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 1.75 }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: "1rem",
              fontWeight: 900,
              color: "var(--fx-surface-text, #0F172A)",
            }}
          >
            Communication Usage
          </Typography>

          <Typography
            sx={{
              mt: 0.25,
              fontSize: "0.82rem",
              color: "var(--fx-surface-textsecondary, #64748B)",
              fontWeight: 500,
            }}
          >
            Monthly usage summary from recorded communication activity.
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: "0.78rem",
            color: "var(--fx-surface-textsecondary, #64748B)",
            fontWeight: 700,
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            border: "1px solid var(--fx-surface-border, #E2E8F0)",
            px: 1.25,
            py: 0.65,
            borderRadius: "999px",
          }}
        >
          Current billing period: {usage?.billingPeriod || "-"}
        </Typography>
      </Stack>

      {isLoading ? (
        <Card
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "16px",
            border: "none",
            bgcolor: "var(--fx-surface-base, #FFFFFF)",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} />
        </Card>
      ) : (
        <>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={2.4}>
              <UsageCard
                label="Call Units"
                value={formatNumber(totals?.totalCallUnits)}
                helper={`${formatNumber(totals?.callInboundUnits)} inbound / ${formatNumber(totals?.callOutboundUnits)} outbound`}
                icon={<PhoneCallbackOutlinedIcon fontSize="small" />}
                color="#0284C7"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <UsageCard
                label="SMS Units"
                value={formatNumber(totals?.totalSmsUnits)}
                helper={`${formatNumber(totals?.smsInboundUnits)} inbound / ${formatNumber(totals?.smsOutboundUnits)} outbound`}
                icon={<SmsOutlinedIcon fontSize="small" />}
                color="#D97706"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <UsageCard
                label="Voicemail"
                value={formatNumber(totals?.voicemailUnits)}
                helper="Voicemail usage units"
                icon={<VoicemailOutlinedIcon fontSize="small" />}
                color="#7C3AED"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <UsageCard
                label="Recordings"
                value={formatNumber(totals?.callRecordingUnits)}
                helper="Call recording usage units"
                icon={<GraphicEqIcon fontSize="small" />}
                color="var(--fx-surface-textsecondary, #64748B)"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2.4}>
              <UsageCard
                label="Total Amount"
                value={formatMoney(totals?.grandTotalAmount, usage?.currency)}
                helper="Total usage amount"
                icon={<AttachMoneyIcon fontSize="small" />}
                color="#16A34A"
              />
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};
